<template>
  <section class="col-sm-12 owner-settings-page-section annulation-section modern-cancel-conditions" id="annulation-section" section-title="Annulation">
    <div class="page-wrapper p-4 space-y-6">
      <!-- En-tête -->
      <div class="space-y-2">
        <h2 class="text-2xl font-bold">Conditions d'annulation</h2>
        <p class="text-muted-foreground">
          Choisissez la politique d'annulation qui convient le mieux à votre activité. Ces conditions seront appliquées
          à toutes vos réservations.
        </p>
      </div>

      <!-- Alerte d'information -->
      <div class="alert alert-info mb-4" role="alert">
        <div class="alert-icon">
          <span class="fa fa-info-circle"></span>
        </div>
        <p class="mb-0">
          <strong>Important :</strong> Pour chaque annulation, il vous sera demandé de confirmer les pénalités. Sans confirmation dans les 24
          heures, celles-ci seront automatiquement appliquées.
        </p>
      </div>

      <!-- Cartes des politiques d'annulation -->
      <form action="" method="POST" v-if="contentLoaded" class="policies-container">
        <div v-for="policy in policies" :key="policy.id" class="policy-card-wrapper">
          <label :for="`condition${policy.id}`" class="cursor-pointer">
            <div class="policy-card" :class="[policy.color, { 'selected': edit.annulationType.id == policy.id }]">
              <div class="card-content-unified">
                <div class="header-content">
                  <input 
                    type="radio" 
                    :id="`condition${policy.id}`" 
                    :value="policy.id" 
                    name="condition" 
                    v-model="edit.annulationType.id"
                    class="radio-input"
                  >
                  <div class="policy-icon" v-html="policy.icon"></div>
                  <div>
                    <h3 class="policy-title" :class="policy.headerColor">
                      {{ policy.name }}
                      <span v-if="policy.recommended" class="recommended-badge">
                        Recommandé
                      </span>
                    </h3>
                    <p class="policy-description">{{ policy.description }}</p>
                  </div>
                </div>
                <div class="rules-container">
                  <div v-for="(rule, index) in policy.rules" :key="index" class="rule-item">
                    <div class="rule-left">
                      <div class="rule-icon" v-html="rule.icon"></div>
                      <div class="rule-period">
                        <i class="fas fa-calendar rule-calendar-icon"></i>
                        <span class="rule-text">{{ rule.period }}</span>
                      </div>
                    </div>
                    <div class="rule-right">
                      <div class="refund-info">
                        <i class="fas fa-euro-sign refund-icon"></i>
                        <span class="refund-text">Remboursé : {{ rule.refund }}</span>
                      </div>
                      <div v-if="rule.penalty !== '0%'" class="penalty-info">
                        <span class="penalty-text">Pénalité : {{ rule.penalty }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </label>
        </div>
      </form>

      <!-- Conseils -->
      <div class="tips-section">
        <h3 class="tips-title">💡 Conseils pour choisir :</h3>
        <ul class="tips-list">
          <li>
            • <strong>Flexibles :</strong> Idéal pour attirer plus de clients et réduire les frictions
          </li>
          <li>
            • <strong>Modérées :</strong> Bon compromis pour la plupart des activités
          </li>
          <li>
            • <strong>Strictes :</strong> Recommandé pour les activités avec des coûts fixes élevés
          </li>
        </ul>
      </div>

      <!-- Bouton de sauvegarde -->
      <button 
        class="btn btn-short btn-primary mt-4" 
        :class="{ 'disabled': !hasChanges }" 
        id="save-button-section-annulation" 
        @click.prevent="sendForm"
      >
        Sauvegarder les modifications
      </button>
    </div>
  </section>
</template>

<script>
import formHandler from "../../mixins/formHandler";

export default {
  name: "cancel-conditions",
  mixins: [formHandler],
  data () {
    return {
      edit: {
        annulationType: {
          id: 2
        }
      },
      annulationType: {
        id: null
      },
      policies: [
        {
          id: 1,
          name: "Conditions Flexibles",
          description: "Idéal pour les clients qui peuvent changer d'avis",
          icon: '<i class="fas fa-check-circle text-success"></i>',
          color: "policy-flexible",
          headerColor: "text-green-800",
          recommended: true,
          rules: [
            {
              period: "Jusqu'à 2 jours avant",
              refund: "100%",
              penalty: "0%",
              icon: '<i class="fas fa-check-circle text-success"></i>',
            },
            {
              period: "1 jour avant le départ",
              refund: "75%",
              penalty: "25%",
              icon: '<i class="fas fa-clock rule-icon-orange"></i>',
            },
            {
              period: "Le jour du départ",
              refund: "50%",
              penalty: "50%",
              icon: '<i class="fas fa-exclamation-triangle rule-icon-red"></i>',
            },
          ],
        },
        {
          id: 2,
          name: "Conditions Modérées",
          description: "Un équilibre entre flexibilité et protection",
          icon: '<i class="fas fa-clock policy-icon-orange"></i>',
          color: "policy-moderate",
          headerColor: "text-orange-800",
          recommended: false,
          rules: [
            {
              period: "Jusqu'à 3 jours avant",
              refund: "100%",
              penalty: "0%",
              icon: '<i class="fas fa-check-circle text-success"></i>',
            },
            {
              period: "Moins de 3 jours avant",
              refund: "75%",
              penalty: "25%",
              icon: '<i class="fas fa-clock rule-icon-orange"></i>',
            },
            {
              period: "La veille du départ",
              refund: "50%",
              penalty: "50%",
              icon: '<i class="fas fa-exclamation-triangle rule-icon-red"></i>',
            },
            {
              period: "Le jour du départ",
              refund: "0%",
              penalty: "100%",
              icon: '<i class="fas fa-times-circle rule-icon-red"></i>',
            },
          ],
        },
        {
          id: 3,
          name: "Conditions Strictes",
          description: "Protection maximale contre les annulations tardives",
          icon: '<i class="fas fa-times-circle policy-icon-red"></i>',
          color: "policy-strict",
          headerColor: "text-red-800",
          recommended: false,
          rules: [
            {
              period: "Jusqu'à 10 jours avant",
              refund: "100%",
              penalty: "0%",
              icon: '<i class="fas fa-check-circle text-success"></i>',
            },
            {
              period: "Moins de 10 jours avant",
              refund: "75%",
              penalty: "25%",
              icon: '<i class="fas fa-clock rule-icon-orange"></i>',
            },
            {
              period: "Moins de 5 jours avant",
              refund: "0%",
              penalty: "100%",
              icon: '<i class="fas fa-times-circle rule-icon-red"></i>',
            },
          ],
        },
      ]
    }
  },
  computed: {
    hasChanges() {
      return this.edit.annulationType.id !== this.annulationType.id;
    }
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.beforeDataFetch()
    })
  },
  methods: {
    transformHook (edit) {
      const content = {
        id: parseInt(edit.annulationType.id),
        ...edit.annulationType,
      }
      return content
    }
  }
}
</script>

<style scoped>
.modern-cancel-conditions {
  padding: 0 !important;
}

.page-wrapper {
  width: 100%;
  max-width: 100%;
}

.p-4 {
  padding: 1.5rem;
}

.space-y-6 > * + * {
  margin-top: 1.5rem;
}

.space-y-2 > * + * {
  margin-top: 0.5rem;
}

.text-2xl {
  font-size: 1.5rem;
  line-height: 2rem;
}

.font-bold {
  font-weight: 700;
}

.text-muted-foreground {
  color: #6b7280;
}

/* Container des politiques */
.policies-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 80%;
  width: 100%;
}

.policy-card-wrapper {
  position: relative;
  width: 100%;
}

.cursor-pointer {
  cursor: pointer;
  width: 100%;
  display: block;
}

/* Cartes des politiques */
.policy-card {
  border: 2px solid transparent;
  border-radius: 0.5rem;
  background: white;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  width: 100%;
}

.policy-card:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.policy-card.selected {
  border-color: #3b82f6;
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.1), 0 2px 4px -1px rgba(59, 130, 246, 0.06);
}

.policy-flexible {
  border-color: #d1fae5;
  background-color: #f0fdf4;
}

.policy-flexible.selected {
  border-color: #10b981;
}

.policy-moderate {
  border-color: #fed7aa;
  background-color: #fffbeb;
}

.policy-moderate.selected {
  border-color: #f59e0b;
}

.policy-strict {
  border-color: #fecaca;
  background-color: #fef2f2;
}

.policy-strict.selected {
  border-color: #ef4444;
}

/* En-tête de carte */
.card-content-unified {
  padding: 1.5rem;
}

.header-content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.radio-input {
  margin-top: 0.25rem;
  flex-shrink: 0;
}

.policy-icon {
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.policy-icon-green {
  color: #059669;
}

.policy-icon-orange {
  color: #d97706;
}

.policy-icon-red {
  color: #dc2626;
}

.policy-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.text-green-800 {
  color: #166534;
}

.text-orange-800 {
  color: #9a3412;
}

.text-red-800 {
  color: #991b1b;
}

.recommended-badge {
  background-color: #dcfce7;
  color: #166534;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}

.policy-description {
  color: #6b7280;
  margin: 0;
  font-size: 0.875rem;
}

/* Contenu de carte */
.rules-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.rule-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: white;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.rule-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.rule-icon {
  flex-shrink: 0;
}

.rule-icon-green {
  color: #059669;
}

.rule-icon-orange {
  color: #f59e0b;
}

.rule-icon-red {
  color: #ef4444;
}

.rule-period {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.rule-calendar-icon {
  color: #6b7280;
  font-size: 0.875rem;
}

.rule-text {
  font-weight: 500;
  font-size: 0.875rem;
}

.rule-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.refund-info {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
}

.refund-icon {
  color: #059669;
  font-size: 0.875rem;
}

.refund-text {
  color: #059669;
  font-weight: 500;
}

.penalty-info {
  font-size: 0.875rem;
}

.penalty-text {
  color: #dc2626;
  font-weight: 500;
}

/* Section conseils */
.tips-section {
  background-color: #f9fafb;
  border-radius: 0.5rem;
  padding: 1rem;
}

.tips-title {
  font-weight: 500;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.tips-list {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tips-list li {
  margin: 0.25rem 0;
}

.tips-list strong {
  color: #111827;
}

/* Responsive */
@media (max-width: 768px) {
  .p-6 {
    padding: 1rem;
  }
  
  .rule-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .rule-right {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
  
  .header-content {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .policy-title {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
}
</style>